/**
 * Every audited action. Adding a value here is additive; never renumber or
 * reuse a name, because historic rows carry the old string forever and an
 * auditor reading a two-year-old log must still be able to interpret it.
 */
export const AUDIT_ACTIONS = [
  // Authentication
  'AUTH_SSO_SUCCESS',
  'AUTH_SSO_REJECTED',
  'AUTH_SESSION_EXPIRED',
  'AUTH_LOGOUT',
  'AUTH_BACKCHANNEL_LOGOUT',
  'AUTH_REVERIFY_FAILED',

  // Query lifecycle
  'QUERY_SUBMITTED',
  'QUERY_DENIED',
  'QUERY_SUCCEEDED',
  'QUERY_FAILED',
  'QUERY_CANCELLED',
  'QUERY_TIMEOUT',

  // Data leaving the system — the events an investigator cares about most
  'EXPORT_REQUESTED',
  'EXPORT_COMPLETED',
  'EXPORT_DENIED',
  'RESULT_VIEWED',

  // Catalog
  'CATALOG_REFRESHED',
  'CATALOG_UNCLASSIFIED_OBJECT_FOUND',

  // Administration
  'DATASOURCE_CREATED',
  'DATASOURCE_UPDATED',
  'DATASOURCE_DELETED',
  'DATASOURCE_TESTED',
  'POLICY_RULE_CREATED',
  'POLICY_RULE_UPDATED',
  'POLICY_RULE_DELETED',
  'POLICY_MODE_CHANGED',
  'USER_ROLE_CHANGED',
  'USER_STATUS_CHANGED',
  'USER_ACCESS_REQUESTED',
  'USER_ACCESS_APPROVED',
  'USER_ACCESS_REJECTED',
  'USER_UNMASK_GRANTED',
  'USER_UNMASK_REVOKED',
  'USER_EXPORT_PRIVILEGE_CHANGED',
  /** Which databases a person may query was changed. */
  'USER_DATASOURCE_ACCESS_CHANGED',
  /** A query returned values that masking rules would normally have hidden. */
  'QUERY_RAN_UNMASKED',

  // Integrity
  'AUDIT_CHAIN_VERIFIED',
  'AUDIT_CHAIN_BROKEN',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_SEVERITIES = ['INFO', 'NOTICE', 'WARNING', 'CRITICAL'] as const;
export type AuditSeverity = (typeof AUDIT_SEVERITIES)[number];

export interface AuditEntry {
  /** Monotonic sequence. Gaps are themselves evidence of tampering. */
  seq: string;
  timestamp: string;
  action: AuditAction;
  severity: AuditSeverity;
  actor: {
    userId: string | null;
    /** Denormalised so the log stays readable after a user row is deleted. */
    email: string | null;
    name: string | null;
    role: string | null;
  };
  sessionId: string | null;
  /** What was acted on: a query run id, datasource key, user id. */
  resource: string | null;
  resourceType: string | null;
  /** Action-specific payload. Never contains raw result data. */
  detail: Record<string, unknown>;
  ip: string | null;
  userAgent: string | null;
  /**
   * SHA-256 over (previous entry's hash + this entry's canonical form).
   * Rewriting or deleting any historic row breaks every hash after it, which
   * is what makes the log tamper-*evident* — it does not prevent a DBA with
   * write access from editing a row, but it guarantees you will know.
   */
  hash: string;
  prevHash: string;
}

export interface AuditQueryFilters {
  from?: string;
  to?: string;
  userId?: string;
  action?: AuditAction[];
  severity?: AuditSeverity[];
  resource?: string;
  /** Substring search across SQL text and detail fields. */
  search?: string;
  cursor?: string;
  limit?: number;
}

export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
  /** Approximate; an exact count over a large audit table is too slow. */
  approxTotal: number;
}

/** Headline numbers for the audit dashboard, over a rolling window. */
export interface AuditSummary {
  since: string;
  /** Query counts keyed by QueryStatus. Absent keys mean zero. */
  totals: Record<string, number>;
  topUsers: Array<{ userId: string; email: string; name: string; queries: number }>;
  exports: number;
  denials: number;
}

/** Result of walking the chain and recomputing every hash. */
export interface ChainVerificationResult {
  ok: boolean;
  entriesChecked: number;
  fromSeq: string;
  toSeq: string;
  durationMs: number;
  /** Populated when ok is false — the first sequence where the chain diverges. */
  brokenAt: {
    seq: string;
    expectedHash: string;
    actualHash: string;
    timestamp: string;
  } | null;
}
